import apiClient from './client';
import { extractResponseData, handleApiError } from './responseHelpers';

export interface SupplierMenuItem {
  id: string;
  supplierId: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  unit: string;
  price: number;
  minOrderQuantity: number;
  allowCustomOrders: boolean;
  currency: string;
  categoryId?: string | null;
  categoryName?: string | null;
  availability: 'in_stock' | 'on_request';
  isFeatured: boolean;
  sourceOfferId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierMenuFilters {
  page?: number;
  limit?: number;
  category_id?: string;
  availability?: string;
  featured?: boolean;
  search?: string;
}

export interface SupplierMenuListResponse {
  data: SupplierMenuItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface UpdateSupplierMenuItemInput {
  price?: number;
  description?: string | null;
  imageUrl?: string | null;
  minOrderQuantity?: number;
  allowCustomOrders?: boolean;
  availability?: 'in_stock' | 'on_request';
  isFeatured?: boolean;
}

export interface CreateSupplierMenuItemInput {
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  unit: string;
  price: number;
  minOrderQuantity?: number;
  allowCustomOrders?: boolean;
  currency?: string;
  categoryId?: string | null;
  categoryName?: string | null;
  availability?: 'in_stock' | 'on_request';
  isFeatured?: boolean;
}

export interface PublicProductDetailResponse {
  id: string;
  supplierId: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  unit: string;
  price: number;
  minOrderQuantity: number;
  allowCustomOrders: boolean;
  currency: string;
  availability: 'in_stock' | 'on_request';
  supplier: {
    id: string;
    name: string;
    rating: number;
    serviceDescription: string;
  };
}

export interface PublicProductListItem extends SupplierMenuItem {
  supplier: {
    id: string;
    name: string;
    rating: number;
    serviceDescription: string;
  };
}

export interface PublicProductListResponse {
  data: PublicProductListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export const supplierMenuApi = {
  list: async (filters?: SupplierMenuFilters): Promise<SupplierMenuListResponse> => {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.category_id) params.append('category_id', filters.category_id);
      if (filters?.availability) params.append('availability', filters.availability);
      if (filters?.featured) params.append('featured', '1');
      if (filters?.search) params.append('search', filters.search);
      const qs = params.toString();
      const url = qs ? `/v1/suppliers/me/menu?${qs}` : '/v1/suppliers/me/menu';
      const response = await apiClient.get<{ data: SupplierMenuItem[]; pagination: SupplierMenuListResponse['pagination'] }>(url);
      return {
        data: response.data?.data ?? response.data ?? [],
        pagination: response.data?.pagination ?? { page: 1, limit: 20, total: 0, total_pages: 0 },
      };
    } catch (error) {
      handleApiError(error);
    }
    return { data: [], pagination: { page: 1, limit: 20, total: 0, total_pages: 0 } };
  },

  getById: async (id: string): Promise<SupplierMenuItem | null> => {
    try {
      const response = await apiClient.get<{ data: SupplierMenuItem }>(`/v1/suppliers/me/menu/${id}`);
      return extractResponseData<SupplierMenuItem>(response) ?? null;
    } catch (error) {
      handleApiError(error);
    }
    return null;
  },

  listBySupplier: async (supplierId: string, filters?: SupplierMenuFilters): Promise<SupplierMenuListResponse> => {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.category_id) params.append('category_id', filters.category_id);
      if (filters?.availability) params.append('availability', filters.availability);
      if (filters?.featured) params.append('featured', '1');
      if (filters?.search) params.append('search', filters.search);
      const qs = params.toString();
      const url = qs ? `/v1/suppliers/${supplierId}/menu?${qs}` : `/v1/suppliers/${supplierId}/menu`;
      const response = await apiClient.get<{ data: SupplierMenuItem[]; pagination: SupplierMenuListResponse['pagination'] }>(url);
      return {
        data: response.data?.data ?? response.data ?? [],
        pagination: response.data?.pagination ?? { page: 1, limit: 20, total: 0, total_pages: 0 },
      };
    } catch (error) {
      handleApiError(error);
    }
    return { data: [], pagination: { page: 1, limit: 20, total: 0, total_pages: 0 } };
  },

  getPublicProductById: async (id: string): Promise<PublicProductDetailResponse | null> => {
    try {
      const response = await apiClient.get<{ data: PublicProductDetailResponse }>(`/v1/products/${id}`);
      return extractResponseData<PublicProductDetailResponse>(response) ?? null;
    } catch (error) {
      handleApiError(error);
    }
    return null;
  },

  listPublicProducts: async (filters?: SupplierMenuFilters): Promise<PublicProductListResponse> => {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.category_id) params.append('category_id', filters.category_id);
      if (filters?.availability) params.append('availability', filters.availability);
      if (filters?.featured) params.append('featured', '1');
      if (filters?.search) params.append('search', filters.search);
      const qs = params.toString();
      const url = qs ? `/v1/products?${qs}` : '/v1/products';
      const response = await apiClient.get<{ data: PublicProductListItem[]; pagination: PublicProductListResponse['pagination'] }>(url);
      return {
        data: response.data?.data ?? [],
        pagination: response.data?.pagination ?? { page: 1, limit: 20, total: 0, total_pages: 0 },
      };
    } catch (error) {
      handleApiError(error);
    }
    return { data: [], pagination: { page: 1, limit: 20, total: 0, total_pages: 0 } };
  },

  update: async (id: string, input: UpdateSupplierMenuItemInput): Promise<SupplierMenuItem | null> => {
    try {
      const response = await apiClient.patch<{ data: SupplierMenuItem }>(`/v1/suppliers/me/menu/${id}`, input);
      return extractResponseData<SupplierMenuItem>(response) ?? null;
    } catch (error) {
      handleApiError(error);
    }
    return null;
  },

  create: async (input: CreateSupplierMenuItemInput): Promise<SupplierMenuItem | null> => {
    try {
      const payload = {
        name: input.name,
        description: input.description ?? null,
        image_url: input.imageUrl ?? null,
        unit: input.unit,
        price: input.price,
        min_order_quantity: input.minOrderQuantity,
        allow_custom_orders: input.allowCustomOrders,
        currency: input.currency,
        category_id: input.categoryId ?? null,
        category_name: input.categoryName ?? null,
        availability: input.availability,
        is_featured: input.isFeatured,
      };
      const response = await apiClient.post<{ data: SupplierMenuItem }>('/v1/suppliers/me/menu', payload);
      return extractResponseData<SupplierMenuItem>(response) ?? null;
    } catch (error) {
      handleApiError(error);
    }
    return null;
  },

  delete: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/v1/suppliers/me/menu/${id}`);
    } catch (error) {
      handleApiError(error);
    }
  },

  adminListBySupplier: async (supplierId: string, filters?: SupplierMenuFilters): Promise<SupplierMenuListResponse> => {
    try {
      const params = new URLSearchParams();
      if (filters?.page) params.append('page', String(filters.page));
      if (filters?.limit) params.append('limit', String(filters.limit));
      if (filters?.category_id) params.append('category_id', filters.category_id);
      if (filters?.availability) params.append('availability', filters.availability);
      if (filters?.featured) params.append('featured', '1');
      if (filters?.search) params.append('search', filters.search);
      const qs = params.toString();
      const url = qs ? `/v1/admin/suppliers/${supplierId}/menu?${qs}` : `/v1/admin/suppliers/${supplierId}/menu`;
      const response = await apiClient.get<{ data: SupplierMenuItem[]; pagination: SupplierMenuListResponse['pagination'] }>(url);
      return {
        data: response.data?.data ?? [],
        pagination: response.data?.pagination ?? { page: 1, limit: 20, total: 0, total_pages: 0 },
      };
    } catch (error) {
      handleApiError(error);
    }
    return { data: [], pagination: { page: 1, limit: 20, total: 0, total_pages: 0 } };
  },

  adminUpdateBySupplier: async (supplierId: string, itemId: string, input: UpdateSupplierMenuItemInput): Promise<SupplierMenuItem | null> => {
    try {
      const response = await apiClient.patch<{ data: SupplierMenuItem }>(`/v1/admin/suppliers/${supplierId}/menu/${itemId}`, input);
      return extractResponseData<SupplierMenuItem>(response) ?? null;
    } catch (error) {
      handleApiError(error);
    }
    return null;
  },

  adminDeleteBySupplier: async (supplierId: string, itemId: string): Promise<void> => {
    try {
      await apiClient.delete(`/v1/admin/suppliers/${supplierId}/menu/${itemId}`);
    } catch (error) {
      handleApiError(error);
    }
  },

  adminCreateBySupplier: async (supplierId: string, input: CreateSupplierMenuItemInput): Promise<SupplierMenuItem | null> => {
    try {
      const payload = {
        name: input.name,
        description: input.description ?? null,
        image_url: input.imageUrl ?? null,
        unit: input.unit,
        price: input.price,
        min_order_quantity: input.minOrderQuantity,
        allow_custom_orders: input.allowCustomOrders,
        currency: input.currency,
        category_id: input.categoryId ?? null,
        category_name: input.categoryName ?? null,
        availability: input.availability,
        is_featured: input.isFeatured,
      };
      const response = await apiClient.post<{ data: SupplierMenuItem }>(`/v1/admin/suppliers/${supplierId}/menu`, payload);
      return extractResponseData<SupplierMenuItem>(response) ?? null;
    } catch (error) {
      handleApiError(error);
    }
    return null;
  },
};
